import React from "react";
import { useIntl, FormattedMessage } from "react-intl";
import styled from "styled-components";
import QRCode from "./QRCode";
import { DevicesGraphic, AppleAppStore, GooglePlayStore } from "./Icons";
import { useBrand } from "../../../../../../common/providers/brand/brand-context";
import { styledSmallMediaQuery } from "../../../default-mobile/common/mobileBreakpoints";
import { cloud } from "@vitalsource/vstui/colors";
import { primaryShade } from "../../../../../../common/ui/Styles";

const Styled = {
  Container: styled.div`
    background-color: #ffffff;
    border-radius: 4px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  `,
  Body: styled.div`
    display: flex;
    padding: 112px 72px 55px 72px;
    ${styledSmallMediaQuery({
      alignItems: "center",
      flexDirection: "column",
    })};
  `,
  DevicesGraphic: styled(DevicesGraphic)`
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
  `,
  InlineBadges: styled.div`
    display: flex;
    margin-top: 24px;
    > * {
      margin-right: 8px;
    }
  `,
  Footer: styled.div`
    font-size: 1.3rem;
    line-height: 24px;
    text-align: center;
    padding-bottom: 16px;
  `,
  QRCode: styled(QRCode)`
    margin-left: auto;
    padding: 8px;
    ${styledSmallMediaQuery({
      margin: "auto",
    })};
  `,
  Title: styled.h3`
    font-size: 32px;
    line-height: 38px;
    padding-bottom: 8px;
    padding-top: 26px;
    font-weight: 500;
    color: ${primaryShade(6)};
  `,
  Subtitle: styled.h4`
    font-size: 20px;
    line-height: 24px;
    padding-bottom: 8px;
    font-weight: 500;
    color: ${primaryShade(6)};
    margin-top: 34px;
  `,
  Text: styled.div`
    color: ${cloud.getShade(10)};
    font-size: 16px;
    line-height: 19px;
    text-align: left;
    font-weight: 400;
  `,
  Instructions: styled.div`
    display: flex;
    flex-direction: column;
    padding-right: 35px;
    ${styledSmallMediaQuery({
      paddingRight: 0,
      paddingBottom: "50px",
    })};
  `,
};

interface InstructionsProps {
  className?: string;
}

const Instructions: React.FC<InstructionsProps> = ({ className }) => {
  const intl = useIntl();
  const brand = useBrand();

  const iosUrl: string = brand.getProp("DownloadOffline.ios_url");
  const androidUrl: string = brand.getProp("DownloadOffline.android_url");
  const bookshelfLabel: string = brand.getProp("appName");

  const brandName: string =
    bookshelfLabel && bookshelfLabel.toLowerCase() !== "bookshelf"
      ? bookshelfLabel
      : "Bookshelf";

  return (
    <Styled.Container className={className}>
      <Styled.DevicesGraphic />
      <Styled.Body>
        <Styled.Instructions>
          <Styled.Title>
            {intl.formatMessage({
              id: "QRDownloadApps.loginInstantly",
            })}
          </Styled.Title>
          <Styled.Text>
            {intl.formatMessage({
              id: "QRDownloadApps.scanPersonalizedCode",
            })}
          </Styled.Text>
          <Styled.Subtitle>
            {intl.formatMessage(
              {
                id: "QRDownloadApps.dontHaveBookshelfApp",
              },
              { brandName }
            )}
          </Styled.Subtitle>
          <Styled.Text>
            {intl.formatMessage(
              {
                id: "QRDownloadApps.scanCodeToDownloadApp",
              },
              { brandName }
            )}
          </Styled.Text>
          <Styled.InlineBadges>
            {iosUrl && (
              <a
                rel="noopener noreferrer"
                href={iosUrl}
                target="_blank"
                aria-label={intl.formatMessage({
                  id: "QRDownloadApps.appleAppStore",
                })}
              >
                <AppleAppStore />
              </a>
            )}
            {androidUrl && (
              <a
                rel="noopener noreferrer"
                href={androidUrl}
                target="_blank"
                aria-label={intl.formatMessage({
                  id: "QRDownloadApps.googlePlayAppStore",
                })}
              >
                <GooglePlayStore />
              </a>
            )}
          </Styled.InlineBadges>
        </Styled.Instructions>
        <Styled.QRCode />
      </Styled.Body>
      <Styled.Footer>
        <FormattedMessage
          id="QRDownloadApps.supportedAppVersion"
          values={{ version: "10.1" }}
        />
      </Styled.Footer>
    </Styled.Container>
  );
};

export default Instructions;
